import ReactECharts from 'echarts-for-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.ts';
import { useFilterStore } from '../stores/filter-store.ts';
import { fmtCurrency, fmtCurrencyCompact, fmtNumber, WOCHENTAGE_LANG } from '../lib/format.ts';
import { COLORS, axisStyle, tooltipStyle } from '../lib/echarts-theme.ts';
import { useIsDark } from '../lib/useIsDark.ts';
import { ChartSkeleton } from '../components/Skeleton.tsx';

const STUNDEN = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const WT_KURZ = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

// Farbrampen: hell = von fast-weiß nach tiefblau; dunkel = von Karten-
// Hintergrund-nah nach hellem Brand-Blau (Glut-Logik: heiß leuchtet).
const RAMP_LIGHT = ['#eff6ff', '#bfdbfe', '#60a5fa', '#1f97f5', '#0b62a8', '#072845'];
const RAMP_DARK = ['#16233a', '#0d3a63', '#0e5c94', '#1f97f5', '#7cc4ff', '#d3ecff'];

export function HeatmapChart() {
  const filters = useFilterStore((s) => s.filters);
  const addFilter = useFilterStore((s) => s.addFilter);
  const dark = useIsDark();
  const { data, isLoading } = useQuery({
    queryKey: ['heatmap', filters],
    queryFn: () => api.heatmap({ filters }),
  });

  if (isLoading) return <ChartSkeleton height={280} />;

  // Leere Randstunden (nachts) abschneiden: angezeigt wird nur der Bereich
  // von der ersten bis zur letzten Stunde mit Umsatz — Öffnungszeiten statt
  // 24h-Raster, die Zellen werden dadurch deutlich größer und lesbarer.
  const activeHours = (data ?? []).filter((c) => c.umsatz > 0).map((c) => c.stunde);
  const hMin = activeHours.length ? Math.min(...activeHours) : 0;
  const hMax = activeHours.length ? Math.max(...activeHours) : 23;
  const hours = STUNDEN.slice(hMin, hMax + 1);

  const cells: Array<[number, number, number, number]> = (data ?? [])
    .filter((c) => c.stunde >= hMin && c.stunde <= hMax)
    .map((c) => [c.stunde - hMin, c.wochentag - 1, c.umsatz, c.anz_bons]);
  const max = Math.max(...cells.map((c) => c[2]), 1);

  const option = {
    grid: { top: 10, right: 20, bottom: 60, left: 50, containLabel: true },
    tooltip: {
      ...tooltipStyle(),
      position: 'top',
      formatter: (p: { value: [number, number, number, number] }) => {
        const [hIdx, wt, umsatz, bons] = p.value;
        const stunde = hIdx + hMin;
        return `<div style="font-size:12px">
          <div style="font-weight:600;margin-bottom:4px">${WOCHENTAGE_LANG[wt + 1]} ${String(stunde).padStart(2, '0')}:00</div>
          <div style="font-variant-numeric:tabular-nums">${fmtCurrency(umsatz)}</div>
          <div style="color:#94a3b8;font-size:11px;margin-top:2px">${fmtNumber(bons)} Bons</div>
        </div>`;
      },
    },
    xAxis: {
      type: 'category',
      data: hours,
      ...axisStyle(),
      splitArea: { show: false },
      axisLabel: { ...axisStyle().axisLabel, formatter: (v: string) => `${v}h` },
    },
    yAxis: {
      type: 'category',
      data: WT_KURZ,
      ...axisStyle(),
      axisLine: { show: false },
      splitArea: { show: false },
    },
    visualMap: {
      min: 0,
      max,
      // Farbe explizit aus Dim 2 (Umsatz) — Dim 3 trägt die Bon-Anzahl
      // nur für den Tooltip mit und darf die Skala nicht kapern.
      dimension: 2,
      calculable: true,
      orient: 'horizontal',
      left: 'center',
      bottom: 0,
      itemWidth: 14,
      itemHeight: 180,
      text: ['Hoch', 'Niedrig'],
      textStyle: {
        color: dark ? COLORS.darkTextMuted : COLORS.textMuted,
        fontSize: 10.5,
        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
      },
      formatter: (v: number) => fmtCurrencyCompact(v),
      inRange: { color: dark ? RAMP_DARK : RAMP_LIGHT },
    },
    series: [
      {
        type: 'heatmap',
        data: cells,
        itemStyle: {
          borderRadius: 1,
          // Fugenfarbe = Karten-Hintergrund, damit das Raster im jeweiligen
          // Theme "gestanzt" wirkt statt weiß zu leuchten.
          borderColor: dark ? '#0f172a' : '#ffffff',
          borderWidth: 2,
        },
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowColor: dark ? 'rgba(124,196,255,0.4)' : 'rgba(15,23,42,0.3)',
          },
        },
        progressive: 1000,
      },
    ],
  };

  return (
    <ReactECharts
      option={option}
      style={{ height: 280 }}
      opts={{ renderer: 'canvas' }}
      notMerge={true}
      onEvents={{
        click: (params: { value: [number, number, number] }) => {
          const [hIdx, wt] = params.value;
          addFilter('wochentag', wt + 1);
          addFilter('stunde', hIdx + hMin);
        },
      }}
    />
  );
}
